// import type React from "react"
import { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { getWsBaseUrl, mediaUrl, sameChat, isGroupConversation, isGroupJid } from "../utils/wsUrl";
import { EMOJI_CATEGORIES } from "../utils/emojiCategories";
import { publishInboxUnread, readCachedInboxUnread } from "../utils/inboxUnread";
import { playInboxChime } from "../utils/inboxSound";
import "./ChatLayer.css";

const AvatarFallback = ({ user, size, className = "" }) => {
    const style = { width: size, height: size, fontSize: size <= 32 ? "0.75rem" : "1.125rem" };

    if (isGroupConversation(user)) {
        return (
            <div className={`wa-avatar group ${className}`.trim()} style={style}>
                <Icon icon="mdi:account-group" width={size <= 32 ? 16 : 22} />
            </div>
        );
    }

    if (user?.type === "instagram") {
        return (
            <div className={`wa-avatar instagram ${className}`.trim()} style={style}>
                <Icon icon="mdi:instagram" width={size <= 32 ? 16 : 22} />
            </div>
        );
    }

    if (user?.type === "facebook") {
        return (
            <div className={`wa-avatar facebook ${className}`.trim()} style={style}>
                <Icon icon="mdi:facebook" width={size <= 32 ? 16 : 22} />
            </div>
        );
    }

    return (
        <div className={`wa-avatar ${getAvatarClass(user?.type)} ${className}`.trim()} style={style}>
            {getInitials(user?.name || user?.avatar)}
        </div>
    );
};

const getMediaUrl = (url) => mediaUrl(url);

/** Highlight case-insensitive query matches inside plain text (safe for React). */
const highlightMatch = (text, query) => {
    const raw = String(text ?? "");
    const q = String(query ?? "").trim();
    if (!raw || !q) return raw;
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = raw.split(new RegExp(`(${escaped})`, "gi"));
    return parts.map((part, i) =>
        part.toLowerCase() === q.toLowerCase() ? (
            <mark key={i} className="wa-search-highlight">
                {part}
            </mark>
        ) : (
            <span key={i}>{part}</span>
        )
    );
};

const readUserPref = (key, fallback = true) => {
    try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        if (u[key] === undefined || u[key] === null) return fallback;
        return !(u[key] === 0 || u[key] === false || u[key] === "0" || u[key] === "false");
    } catch {
        return fallback;
    }
};

const mediaPreviewLabel = (msg_type, text = "") => {
    const labels = {
        audio: "🎤 Voice message",
        image: "📷 Photo",
        video: "🎬 Video",
        document: "📎 Document",
        call_voice: "📞 Missed voice call",
        call_video: "🎥 Missed video call",
        attachment: "📎 Media",
    };
    if (labels[msg_type]) return labels[msg_type];
    if (text && String(text).includes("/media/")) return "📎 Media";
    return text || "📎 Media";
};

const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (parts[0]?.[0] || "?").toUpperCase();
};

const getAvatarClass = (type) => {
    if (type === "facebook") return "facebook";
    if (type === "instagram") return "instagram";
    return "whatsapp";
};

const resolvePhotoUrl = (photo) => {
    if (!photo) return null;
    const url = mediaUrl(photo);
    return url || null;
};

const isRenderableMediaUrl = (url) => {
    if (!url) return false;
    return (
        url.startsWith("blob:") ||
        url.startsWith("data:") ||
        url.includes("/media/") ||
        /^https?:\/\//i.test(url)
    );
};

const resolveMsgType = (message) => {
    const t = String(message?.msg_type || "text").toLowerCase();
    if (t && t !== "text") return t;
    const text = String(message?.text || "");
    if (!text) return "text";
    if (/missed video call|video call/i.test(text) && /🎥|call/i.test(text)) return "call_video";
    if (/missed voice call|voice call|📞/i.test(text) && /call/i.test(text)) return "call_voice";
    if (/\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(text)) return "image";
    if (/\.(mp4|webm|mov|mkv)(\?|$)/i.test(text)) return "video";
    if (/\.(ogg|opus|mp3|wav|m4a|aac)(\?|$)/i.test(text)) return "audio";
    if (/\.(pdf|docx?|xlsx?|pptx?|zip|rar|txt)(\?|$)/i.test(text)) return "document";
    if (text.includes("/media/") && /\.(jpe?g|png|gif|webp)/i.test(text)) return "image";
    return t || "text";
};

const makeTempMessageId = () =>
    `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const SELECTED_CHAT_KEY = "wa_rca_selected_chat_id";
const MESSAGE_PAGE_SIZE = 30;
const CHAT_PAGE_SIZE = 10;
const SELECTED_INSTANCE_KEY = "wa_rca_selected_instance";



const mergeConversationIntoList = (list, conversation) =>
    list.map((item) => (item.id === conversation.id ? { ...item, ...conversation } : item));

const getConversationInstanceId = (user) => {
    const raw = user?.client_id || user?.instance_id || null;
    if (raw == null || raw === "") return null;
    return String(raw);
};

const isActiveInstance = (instance) =>
    Number(instance?.status) === 1 ||
    instance?.status === true ||
    Number(instance?.is_active) === 1 ||
    instance?.is_active === true;

const ChatMediaImage = ({ src }) => {
    const [failed, setFailed] = useState(false);
    if (!src) return null;
    if (failed) {
        return (
            <a href={src} target="_blank" rel="noreferrer" className="wa-document-link">
                📷 Open image
            </a>
        );
    }
    return (
        <img
            src={src}
            alt="Shared"
            className="wa-media-image"
            onError={() => setFailed(true)}
        />
    );
};

const ChatAvatar = ({ user, size = 49, className = "", onClick }) => {
    const photo = resolvePhotoUrl(user?.profile_photo_url || user?.profile_photo);
    const [imgFailed, setImgFailed] = useState(false);
    const style = { width: size, height: size, fontSize: size <= 32 ? "0.75rem" : "1.125rem" };

    useEffect(() => {
        setImgFailed(false);
    }, [photo, user?.id]);

    const photoEl = photo && !imgFailed ? (
        <img
            src={photo}
            alt={user?.name || "Contact"}
            className={`wa-avatar-img ${className}`.trim()}
            style={style}
            onError={() => setImgFailed(true)}
        />
    ) : (
        <AvatarFallback user={user} size={size} className={className} />
    );

    if (onClick) {
        return (
            <button
                type="button"
                className="wa-avatar-btn"
                style={style}
                onClick={onClick}
                aria-label={user?.name || "Contact"}
            >
                {photoEl}
            </button>
        );
    }

    return photoEl;
};

const ChatSystem = () => {
    const messagesEndRef = useRef(null)
    const messagesContainerRef = useRef(null)
    const stickToBottomRef = useRef(true)
    const loadingOlderRef = useRef(false)
    const conversationsLoadedRef = useRef(false)
    const [users, setUsers] = useState([])

    const [totalParticipants, setTotalParticipants] = useState(null)
    const [selectedUser, setSelectedUser] = useState(null)
    const [messages, setMessages] = useState([])
    const [hasMoreMessages, setHasMoreMessages] = useState(false)
    const [loadingOlder, setLoadingOlder] = useState(false)

    const [instances, setInstances] = useState([])
    const [selectedInstance, setSelectedInstance] = useState(
        () => sessionStorage.getItem(SELECTED_INSTANCE_KEY) || "all"
    )
    const [newMessage, setNewMessage] = useState("")
    const [searchTerm, setSearchTerm] = useState("")
    const [chatKindFilter, setChatKindFilter] = useState("all") // all | direct | groups
    const [channelFilter, setChannelFilter] = useState("all") // all | whatsapp | facebook | instagram
    const [groupResults, setGroupResults] = useState([])
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const [emojiOpen, setEmojiOpen] = useState(false)
    const [emojiCategory, setEmojiCategory] = useState(EMOJI_CATEGORIES[0].id)
    const [reactPicker, setReactPicker] = useState(null) // { messageId, chatId, x, y }
    const [attachMenuOpen, setAttachMenuOpen] = useState(false)
    const [libraryPickerOpen, setLibraryPickerOpen] = useState(false)
    const [libraryItems, setLibraryItems] = useState([])
    const [libraryLoading, setLibraryLoading] = useState(false)
    const [libraryKind, setLibraryKind] = useState("all")
    /** chat_id -> { is_typing, is_recording, typer_name } */
    const [typingByChat, setTypingByChat] = useState({})
    const typingPauseTimerRef = useRef(null)
    const lastTypingSentRef = useRef({ chatId: null, state: null, at: 0 })
    const [incomingCall, setIncomingCall] = useState(null) // ringing call payload
    const [callActionLoading, setCallActionLoading] = useState(false)
    const QUICK_REACTS = ["👍", "❤️", "😂", "😮", "😢", "🙏", "🔥", "✅"]
    const LIBRARY_KINDS = [
        { id: "all", label: "All" },
        { id: "image", label: "Images" },
        { id: "video", label: "Videos" },
        { id: "audio", label: "Audio" },
        { id: "document", label: "Docs" },
    ]

    // Voice recording states
    const [isRecording, setIsRecording] = useState(false)
    const [recordingDuration, setRecordingDuration] = useState(0)
    const [audioBlob, setAudioBlob] = useState(null)
    const mediaRecorderRef = useRef(null)
    const audioChunks = useRef([])
    const timerIntervalRef = useRef(null)
    const fileInputRef = useRef(null)
    const messageInputRef = useRef(null)
    const emojiPickerRef = useRef(null)
    const attachMenuRef = useRef(null)
    const [filteredUsers, setFilteredUsers] = useState(users);
    const [myProfilePhoto, setMyProfilePhoto] = useState(null);
    const [contactResults, setContactResults] = useState([]);
    const [messageSearchResults, setMessageSearchResults] = useState([]);
    const [phoneSuggestion, setPhoneSuggestion] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [messageSearchLoading, setMessageSearchLoading] = useState(false);
    /** When opening a chat from search: highlight query + scroll to message */
    const [searchFocus, setSearchFocus] = useState(null); // { query, messageId }
    const searchFocusClearRef = useRef(null);
    const [listLoading, setListLoading] = useState(true);
    const [inboxUnreadTotal, setInboxUnreadTotal] = useState(() => readCachedInboxUnread());
    const [hasMoreChats, setHasMoreChats] = useState(false);
    const [loadingMoreChats, setLoadingMoreChats] = useState(false);
    const chatListRef = useRef(null);
    const loadingMoreChatsRef = useRef(false);
    const chatCursorRef = useRef({ time: null, id: null });
    const hasMoreChatsRef = useRef(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [contactInfoOpen, setContactInfoOpen] = useState(false);
    const [menuLoading, setMenuLoading] = useState(false);
    const [quickReplies, setQuickReplies] = useState([]);
    const [toast, setToast] = useState(null); // { message, tone: 'info'|'danger'|'success' }
    const [sendNotice, setSendNotice] = useState(null);
    const toastTimerRef = useRef(null);
    const menuRef = useRef(null);

    const showToast = (message, tone = "info") => {
        if (!message) return;
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ message, tone });
        toastTimerRef.current = setTimeout(() => setToast(null), 4200);
    };

    const insertEmoji = (emoji) => {
        const input = messageInputRef.current;
        if (!input) {
            setNewMessage((prev) => prev + emoji);
            return;
        }

        const start = input.selectionStart ?? newMessage.length;
        const end = input.selectionEnd ?? newMessage.length;
        const next = newMessage.slice(0, start) + emoji + newMessage.slice(end);
        setNewMessage(next);

        requestAnimationFrame(() => {
            input.focus();
            const caret = start + emoji.length;
            input.setSelectionRange(caret, caret);
        });
    };

    useEffect(() => {
        if (!emojiOpen) return;
        const onDocClick = (e) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
                setEmojiOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [emojiOpen]);

    useEffect(() => {
        if (!attachMenuOpen) return;
        const onDocClick = (e) => {
            if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
                setAttachMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [attachMenuOpen]);

    useEffect(() => {
        if (!reactPicker) return undefined;
        const onDocClick = () => setReactPicker(null);
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [reactPicker]);

    const openReactPicker = (message, evt) => {
        const rect = evt?.currentTarget?.getBoundingClientRect?.();
        setReactPicker({
            messageId: message.id,
            chatId: message.chat_id || selectedUserRef.current?.id,
            x: rect ? rect.left : 80,
            y: rect ? rect.top - 8 : 120,
        });
    };

    const sendReaction = async (emoji) => {
        const picker = reactPicker;
        setReactPicker(null);
        if (!picker?.messageId || !picker?.chatId) return;
        try {
            const response = await apiCall({
                url: "social/react-message",
                method: "POST",
                body: {
                    chat_id: picker.chatId,
                    message_id: picker.messageId,
                    emoji,
                },
            });
            if (!response?.success) {
                showToast(response?.msg || "Could not send reaction", "danger");
                return;
            }
            const reactions = Array.isArray(response.data?.reactions)
                ? response.data.reactions
                : [];
            setMessages((prev) =>
                prev.map((m) =>
                    String(m.id) === String(picker.messageId) ? { ...m, reactions } : m
                )
            );
        } catch (err) {
            showToast(err?.message || "Could not send reaction", "danger");
        }
    };

    // const handleSendMessage = async (msg_type) => {
    //     if (!selectedUser) return
    //     if (msg_type === "text" && newMessage.trim() === "") return

    //     // Construct the base message object
    //     const message = {
    //         type: selectedUser.type,
    //         chat_id: selectedUser.id,
    //         sender: "user",
    //         timestamp: new Date(),
    //         read: false,
    //         recipient_id: selectedUser.sid,
    //         msg_type: msg_type,
    //     }

    //     if (msg_type === "text") {
    //         // For text messages, include the message text
    //         message.content = newMessage
    //         message.text = newMessage
    //     } else if (msg_type === "attachment" && audioBlob) {
    //         // For audio messages, include the audio Blob or URL
    //         const ab = URL.createObjectURL(audioBlob)
    //         message.content = ab
    //         message.text = ab
    //     }

    //     try {
    //         const response = await apiCall({
    //             url: `social/send-message`,
    //             method: "POST",
    //             body: message,
    //         })

    //         if (response.success) {
    //             // Clear the input field for text messages
    //             if (msg_type === "text") {
    //                 setNewMessage("")
    //             }

    //             // Reset audio blob after sending
    //             if (msg_type === "attachment") {
    //                 setAudioBlob(null)
    //             }

    //             // Update last message in user list
    //             setUsers(
    //                 users.map((user) =>
    //                     user.id === selectedUser?.id
    //                         ? {
    //                             ...user,
    //                             lastMessage: msg_type === "text" ? newMessage : "Audio message",
    //                             lastMessageTime: new Date(),
    //                         }
    //                         : user,
    //                 ),
    //             )

    //             // Scroll to the bottom of the chat
    //             if (messagesEndRef.current) {
    //                 messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    //             }

    //             // Add the message to the messages list
    //             setMessages((prevMessages) => [...prevMessages, message])
    //         }
    //     } catch (error) {
    //         console.log(error.message)
    //     }
    // }

    const bumpConversation = (matcher, patch) => {
        setUsers((list) =>
            [...list]
                .map((user) => {
                    let match = false;
                    if (typeof matcher === "function") {
                        match = matcher(user);
                    } else if (matcher && typeof matcher === "object") {
                        match =
                            sameChat(user, matcher) ||
                            String(user.id) === String(matcher.id) ||
                            (matcher.sid && user.sid === matcher.sid);
                    } else {
                        match = user.sid === matcher;
                    }
                    return match ? { ...user, ...patch } : user;
                })
                .sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime))
        );
    };

    const appendMessage = (incoming) => {
        if (!incoming?.id && !incoming?.text && !incoming?.temp_id) return;
        stickToBottomRef.current = true;

        setMessages((prevMessages) => {
            if (incoming.id && prevMessages.some((m) => String(m.id) === String(incoming.id))) {
                return prevMessages;
            }

            // Merge server/WS delivery into a matching optimistic pending bubble
            if (incoming.sender === "user" && incoming.id) {
                const pendingIdx = prevMessages.findIndex((m) => {
                    if (m.send_status !== "pending" && m.send_status !== "failed") return false;
                    if (m.chat_id != null && incoming.chat_id != null && String(m.chat_id) !== String(incoming.chat_id)) {
                        return false;
                    }
                    if (m.msg_type && incoming.msg_type && m.msg_type !== incoming.msg_type) return false;
                    if (m.text && incoming.text && String(m.text) === String(incoming.text)) return true;
                    if (m.temp_id && incoming.temp_id && m.temp_id === incoming.temp_id) return true;
                    return false;
                });
                if (pendingIdx >= 0) {
                    const next = [...prevMessages];
                    const prev = next[pendingIdx];
                    if (prev._localObjectUrl && prev._localObjectUrl.startsWith("blob:")) {
                        try {
                            URL.revokeObjectURL(prev._localObjectUrl);
                        } catch {
                            /* ignore */
                        }
                    }
                    next[pendingIdx] = {
                        ...incoming,
                        send_status: "sent",
                        temp_id: prev.temp_id || null,
                    };
                    return next;
                }
            }

            return [...prevMessages, incoming];
        });
    };

    const patchMessage = (tempId, patch) => {
        setMessages((prev) =>
            prev.map((m) => (m.temp_id === tempId || String(m.id) === String(tempId) ? { ...m, ...patch } : m))
        );
    };

    const resolveSendClientId = () => {
        if (selectedInstance && selectedInstance !== "all") return selectedInstance;
        return getConversationInstanceId(selectedUser);
    };

    const isMetaChannel = (user = selectedUser) => {
        const t = String(user?.type || "").toLowerCase();
        return t === "facebook" || t === "instagram";
    };

    const appendFormFields = (formData, msg_type, extra = {}) => {
        const clientId = resolveSendClientId();
        const group = isGroupConversation(selectedUser);
        const meta = isMetaChannel(selectedUser);
        // Messenger/IG use Page-scoped user id (sid). WhatsApp uses JID.
        const recipient = meta
            ? selectedUser.sid || selectedUser.recipient_id || ""
            : selectedUser.wa_jid || selectedUser.jid || selectedUser.sid || "";
        formData.append("type", group ? "group" : selectedUser.type || "whatsapp");
        formData.append("chat_id", selectedUser.id);
        formData.append("sender", "user");
        formData.append("timestamp", new Date().toISOString());
        formData.append("read", false);
        formData.append("recipient_id", recipient);
        formData.append("msg_type", msg_type);
        formData.append("is_group", group ? "1" : "0");
        if (group) {
            formData.append("group_id", recipient);
            formData.append("groupData", JSON.stringify({ id: recipient, name: selectedUser.name || "" }));
        }
        if (clientId && !meta) formData.append("client_id", clientId);
        Object.entries(extra).forEach(([key, val]) => formData.append(key, val));
    };

    const handleSendMessage = async (msg_type, file = null, libraryItem = null, opts = {}) => {
        if (!selectedUser) return;

        const retryPayload = opts.retryPayload || null;
        const textContent = retryPayload
            ? String(retryPayload.textContent || "")
            : msg_type === "text"
              ? newMessage.trim()
              : "";
        const sendFile = retryPayload?.file || file;
        const sendLibrary = retryPayload?.libraryItem || libraryItem;
        const sendAudio = retryPayload?.audioBlob || (msg_type === "audio" ? audioBlob : null);
        let resolvedType = retryPayload?.msg_type || msg_type;

        if (resolvedType === "text" && !textContent) return;
        if (
            ["audio", "image", "video", "document"].includes(resolvedType) &&
            !sendFile &&
            !sendAudio &&
            !sendLibrary?.id
        ) {
            return;
        }

        const meta = isMetaChannel(selectedUser);
        const clientId = resolveSendClientId();
        if (!meta && !clientId) {
            showToast("Select an active WhatsApp instance to send messages", "danger");
            return;
        }

        if (sendLibrary?.id) {
            const kind = String(sendLibrary.media_kind || "").toLowerCase();
            resolvedType = ["image", "video", "audio", "document"].includes(kind) ? kind : "document";
        } else if (sendFile) {
            if (sendFile.type?.startsWith("image/")) resolvedType = "image";
            else if (sendFile.type?.startsWith("video/")) resolvedType = "video";
            else if (sendFile.type?.startsWith("audio/")) resolvedType = "audio";
            else if (resolvedType !== "audio") resolvedType = "document";
        } else if (sendAudio) {
            resolvedType = "audio";
        }

        let localObjectUrl = null;
        let optimisticText = textContent;
        if (sendLibrary?.id) {
            optimisticText = sendLibrary.url || sendLibrary.relative_path || sendLibrary.filename || "";
        } else if (sendFile) {
            localObjectUrl = URL.createObjectURL(sendFile);
            optimisticText = localObjectUrl;
        } else if (sendAudio) {
            localObjectUrl = URL.createObjectURL(sendAudio);
            optimisticText = localObjectUrl;
        }

        const tempId = opts.tempId || makeTempMessageId();
        const isRetry = Boolean(opts.tempId);
        const payload = {
            msg_type: resolvedType,
            textContent,
            file: sendFile || null,
            libraryItem: sendLibrary || null,
            audioBlob: sendAudio || null,
        };

        setSendNotice(null);

        if (!isRetry) {
            appendMessage({
                id: tempId,
                temp_id: tempId,
                chat_id: selectedUser.id,
                sender: "user",
                text: optimisticText,
                msg_type: resolvedType,
                timestamp: new Date().toISOString(),
                send_status: "pending",
                is_read: 0,
                wa_message_id: null,
                reactions: [],
                _retry: payload,
                _localObjectUrl: localObjectUrl,
            });
            if (resolvedType === "text") setNewMessage("");
            if (resolvedType === "audio") setAudioBlob(null);
            setLibraryPickerOpen(false);
            setAttachMenuOpen(false);
            sendTypingState("paused");
            bumpConversation(selectedUser, {
                lastMessage: mediaPreviewLabel(resolvedType, optimisticText) || optimisticText,
                lastMessageTime: new Date(),
            });
            requestAnimationFrame(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            });
        } else {
            patchMessage(tempId, { send_status: "pending", send_error: null });
        }

        const formData = new FormData();
        try {
            if (resolvedType === "text") {
                appendFormFields(formData, "text", { content: textContent, text: textContent });
            } else if (resolvedType === "audio" && sendAudio && !sendFile && !sendLibrary?.id) {
                const ext = sendAudio.type?.includes("ogg") ? "ogg" : "webm";
                const fileToSend = new File([sendAudio], `voice.${ext}`, {
                    type: sendAudio.type || "audio/webm",
                });
                formData.append("file", fileToSend);
                appendFormFields(formData, "audio", { content: "", text: "" });
            } else if (sendLibrary?.id) {
                appendFormFields(formData, resolvedType, {
                    content: "",
                    text: sendLibrary.title || sendLibrary.filename || "",
                    media_library_id: String(sendLibrary.id),
                });
            } else if (sendFile) {
                formData.append("file", sendFile);
                appendFormFields(formData, resolvedType, { content: "", text: sendFile.name });
            }

            const response = await apiCall({
                url: `social/send-message`,
                method: "POST",
                body: formData,
                withoutStringify: true,
            });
            if (response.success) {
                const savedMessage = response.data || {};
                const preview = savedMessage.preview || mediaPreviewLabel(savedMessage.msg_type || resolvedType);
                if (localObjectUrl) {
                    try {
                        URL.revokeObjectURL(localObjectUrl);
                    } catch {
                        /* ignore */
                    }
                }
                setMessages((prev) => {
                    const withoutDup = prev.filter(
                        (m) =>
                            !(
                                savedMessage.id &&
                                String(m.id) === String(savedMessage.id) &&
                                m.temp_id !== tempId
                            )
                    );
                    return withoutDup.map((m) =>
                        m.temp_id === tempId
                            ? {
                                  ...savedMessage,
                                  send_status: "sent",
                                  temp_id: tempId,
                                  msg_type: savedMessage.msg_type || resolvedType,
                                  delivery_status: savedMessage.delivery_status || "sent",
                              }
                            : m
                    );
                });
                bumpConversation(selectedUser, {
                    lastMessage: preview || savedMessage.text || textContent,
                    lastMessageTime: new Date(),
                });
            } else {
                const msg = response.msg || "Failed to send message";
                patchMessage(tempId, {
                    send_status: "failed",
                    send_error: msg,
                    _retry: payload,
                });
                if (response.reason === "META_OUTSIDE_WINDOW" || /24 hours/i.test(msg)) {
                    setSendNotice({
                        tone: "warning",
                        title: "Outside Facebook reply window",
                        message: msg,
                    });
                }
            }
        } catch (error) {
            patchMessage(tempId, {
                send_status: "failed",
                send_error: error.message || "Failed to send message",
                _retry: payload,
            });
        }
    };

    const retrySendMessage = (message) => {
        if (!message?._retry || !message.temp_id) return;
        handleSendMessage(message._retry.msg_type, null, null, {
            tempId: message.temp_id,
            retryPayload: message._retry,
        });
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) handleSendMessage("document", file);
        e.target.value = "";
    };

    const sendTypingState = (state) => {
        const chatId = selectedUser?.id;
        if (!chatId) return;
        const t = String(selectedUser?.type || "whatsapp").toLowerCase();
        if (t === "facebook" || t === "instagram") return;

        const now = Date.now();
        const last = lastTypingSentRef.current;
        if (
            last.chatId === chatId &&
            last.state === state &&
            now - last.at < (state === "composing" ? 2500 : 800)
        ) {
            return;
        }
        lastTypingSentRef.current = { chatId, state, at: now };

        apiCall({
            url: "social/typing",
            method: "POST",
            body: { chat_id: chatId, state },
        }).catch(() => {});
    };

    const notifyTypingFromInput = (value) => {
        if (!selectedUser?.id) return;
        const hasText = String(value || "").trim().length > 0;
        if (hasText) {
            sendTypingState("composing");
            if (typingPauseTimerRef.current) clearTimeout(typingPauseTimerRef.current);
            typingPauseTimerRef.current = setTimeout(() => {
                sendTypingState("paused");
            }, 2000);
        } else {
            if (typingPauseTimerRef.current) clearTimeout(typingPauseTimerRef.current);
            sendTypingState("paused");
        }
    };

    const loadMediaLibrary = async (kind = libraryKind) => {
        setLibraryLoading(true);
        try {
            const res = await apiCall({
                url: `features/media-library?kind=${encodeURIComponent(kind)}`,
                method: "GET",
            });
            if (res?.success) setLibraryItems(res.data || []);
            else showToast(res?.msg || "Failed to load media library", "danger");
        } catch (err) {
            showToast(err.message || "Failed to load media library", "danger");
        } finally {
            setLibraryLoading(false);
        }
    };

    const openLibraryPicker = () => {
        setAttachMenuOpen(false);
        setEmojiOpen(false);
        setLibraryKind("all");
        setLibraryPickerOpen(true);
        loadMediaLibrary("all");
    };

    const handleLibraryKindChange = (kind) => {
        setLibraryKind(kind);
        loadMediaLibrary(kind);
    };

    const sendFromLibrary = (item) => {
        if (!item?.id) return;
        const kind = String(item.media_kind || "").toLowerCase();
        const type = ["image", "video", "audio", "document"].includes(kind) ? kind : "document";
        handleSendMessage(type, null, item);
    };

    const rejectIncomingCall = async () => {
        if (!incomingCall?.call_id) return;
        setCallActionLoading(true);
        try {
            const res = await apiCall({
                url: "social/reject-call",
                method: "POST",
                body: {
                    call_id: incomingCall.call_id,
                    chat_id: incomingCall.chat_id,
                    client_id: incomingCall.client_id,
                },
            });
            if (!res?.success) showToast(res?.msg || "Could not reject call", "danger");
            setIncomingCall(null);
        } catch (err) {
            showToast(err.message || "Could not reject call", "danger");
        } finally {
            setCallActionLoading(false);
        }
    };

    const openWhatsAppCall = async (kind = "voice") => {
        if (!selectedUser?.id) return;
        const t = String(selectedUser.type || "whatsapp").toLowerCase();
        if (t !== "whatsapp" && t !== "group") {
            showToast("Calls are only available for WhatsApp chats", "danger");
            return;
        }
        try {
            const res = await apiCall({
                url: `social/call-links?chat_id=${encodeURIComponent(selectedUser.id)}`,
                method: "GET",
            });
            if (!res?.success || !res.data) {
                showToast(res?.msg || "No phone number for this chat", "danger");
                return;
            }
            const deep =
                kind === "video" ? res.data.video_deeplink : res.data.voice_deeplink;
            const isMobile = /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent || "");
            if (isMobile && deep) {
                window.location.href = deep;
            } else {
                // Desktop: open WhatsApp Web / app chat — tap the call icon there
                window.open(res.data.chat_url || res.data.app_url, "_blank", "noopener,noreferrer");
            }
        } catch (err) {
            showToast(err.message || "Could not open WhatsApp call", "danger");
        }
    };

    const renderMessageContent = (message) => {
        const src = getMediaUrl(message.text);
        const type = resolveMsgType(message);
        if (type === "call_voice" || type === "call_video" || /missed (voice|video) call/i.test(String(message.text || ""))) {
            const isVideo = type === "call_video" || /video call/i.test(String(message.text || ""));
            const missed = /missed/i.test(String(message.text || ""));
            return (
                <div className={`wa-call-bubble ${missed ? "missed" : ""}`}>
                    <Icon icon={isVideo ? "mdi:video-outline" : "mdi:phone-outline"} width={20} />
                    <span>{message.text || (isVideo ? "Video call" : "Voice call")}</span>
                </div>
            );
        }
        const canRenderMedia = isRenderableMediaUrl(src);

        if ((type === "image" || type === "sticker") && canRenderMedia) {
            return (
                <>
                    <ChatMediaImage src={src} />
                    {message.caption && <p className="wa-bubble-text">{message.caption}</p>}
                </>
            );
        }

        if (type === "video" && canRenderMedia) {
            return (
                <video controls className="wa-media-video" src={src}>
                    <source src={src} />
                </video>
            );
        }

        if ((type === "audio" || type === "attachment") && canRenderMedia) {
            return (
                <audio controls className="wa-media-audio" src={src}>
                    <source src={src} />
                </audio>
            );
        }

        if (type === "document") {
            if (canRenderMedia && /\.(webm|ogg|mp3|wav|m4a)(\?|$)/i.test(src)) {
                return (
                    <audio controls className="wa-media-audio" src={src}>
                        <source src={src} />
                    </audio>
                );
            }
            if (canRenderMedia) {
                return (
                    <a href={src} target="_blank" rel="noreferrer" className="wa-document-link">
                        📎 {String(message.text || "").split("/").pop() || "Document"}
                    </a>
                );
            }
        }

        const body = searchFocus?.query
            ? highlightMatch(message.text, searchFocus.query)
            : message.text;
        return <p className="wa-bubble-text">{body}</p>;
    };

    const clearSearchFocusSoon = () => {
        if (searchFocusClearRef.current) clearTimeout(searchFocusClearRef.current);
        searchFocusClearRef.current = setTimeout(() => setSearchFocus(null), 12000);
    };

    const selectUser = (user, opts = {}) => {
        if (!user?.id) return;
        const focusQuery = String(opts.focusQuery || "").trim();
        const focusMessageId = opts.focusMessageId != null ? opts.focusMessageId : null;

        if (focusQuery || focusMessageId) {
            setSearchFocus({ query: focusQuery, messageId: focusMessageId });
        } else {
            setSearchFocus(null);
        }

        sessionStorage.setItem(SELECTED_CHAT_KEY, String(user.id));
        setSendNotice(null);
        setSelectedUser({ ...user, unreadCount: 0 });
        setHasMoreMessages(false);
        stickToBottomRef.current = !(focusQuery || focusMessageId);
        getConversationMessages(user.id, { focusQuery, focusMessageId });
        const clearUnread = (list) =>
            list.map((u) => (u.id === user.id ? { ...u, unreadCount: 0 } : u));
        setUsers(clearUnread);
    };

    const formatTime = (date) => {
        date = new Date(date)
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }

    const formatDate = (date) => {
        date = new Date(date)
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        if (date.toDateString() === today.toDateString()) {
            return formatTime(date)
        } else if (date.toDateString() === yesterday.toDateString()) {
            return "Yesterday"
        } else {
            return date.toLocaleDateString([], {
                month: "short",
                day: "numeric",
            })
        }
    }

    const fetchInstances = async () => {
        try {
            const response = await apiCall({
                url: `sessions/get-users-instances`,
                method: "GET",
            });
            const active = (response.data || []).filter(isActiveInstance);
            setInstances(active);

            const saved = sessionStorage.getItem(SELECTED_INSTANCE_KEY);
            const savedOk =
                saved &&
                saved !== "all" &&
                active.some((i) => String(i.client_id) === String(saved));

            // Prefer "all" so the All-channel inbox shows every chat by default.
            // Only restore a specific instance when the user explicitly chose one.
            if (savedOk) {
                setSelectedInstance(String(saved));
            } else {
                setSelectedInstance("all");
                sessionStorage.setItem(SELECTED_INSTANCE_KEY, "all");
            }
        } catch (error) {
            console.error("Error fetching instances:", error);
        }
    };

    const buildConversationListUrl = (extra = {}) => {
        const params = new URLSearchParams({
            limit: String(CHAT_PAGE_SIZE),
            channel: channelFilter || "all",
            kind: chatKindFilter || "all",
        });
        if (selectedInstance && selectedInstance !== "all") {
            params.set("client_id", selectedInstance);
        }
        const q = String(extra.q ?? searchTerm ?? "").trim();
        if (q) params.set("q", q);
        if (extra.before_time && extra.before_id != null) {
            params.set("before_time", String(extra.before_time));
            params.set("before_id", String(extra.before_id));
        }
        return `social/get-conversation-list?${params.toString()}`;
    };

    const rememberChatCursor = (payload, conversations) => {
        const last = conversations[conversations.length - 1];
        const time = payload?.next_before_time || last?.lastMessageTime || null;
        const id = payload?.next_before_id || last?.id || null;
        chatCursorRef.current = { time, id };
        const more = Boolean(payload?.has_more);
        hasMoreChatsRef.current = more;
        setHasMoreChats(more);
    };

    const getConversationList = async (opts = {}) => {
        const { silent = false, merge = false } = opts;
        if (!silent && !merge) setListLoading(true);
        try {
            const response = await apiCall({
                url: buildConversationListUrl(opts),
                method: "GET",
            });
            if (response.success && response.data?.conversations) {
                const conversations = response.data.conversations;
                if (response.data.myProfilePhoto) {
                    setMyProfilePhoto(mediaUrl(response.data.myProfilePhoto) || response.data.myProfilePhoto);
                }

                conversationsLoadedRef.current = true;
                if (merge) {
                    setUsers((prev) => {
                        const incomingById = new Map(conversations.map((c) => [c.id, c]));
                        const updated = prev.map((u) =>
                            incomingById.has(u.id) ? { ...u, ...incomingById.get(u.id) } : u
                        );
                        const known = new Set(prev.map((u) => u.id));
                        const extras = conversations.filter((c) => !known.has(c.id));
                        return [...extras, ...updated].sort(
                            (a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
                        );
                    });
                } else {
                    setUsers(conversations);
                    rememberChatCursor(response.data, conversations);
                }
                if (response.data.total != null) {
                    const n = Number(response.data.total);
                    if (Number.isFinite(n)) setTotalParticipants(n);
                }
                if (response.data.unreadTotal != null) {
                    publishInboxUnread(Number(response.data.unreadTotal) || 0);
                }
            }
        } catch (error) {
            console.error("Error fetching conversations:", error);
        } finally {
            if (!silent && !merge) setListLoading(false);
        }
    };

    const loadMoreChats = async () => {
        if (!hasMoreChatsRef.current || loadingMoreChatsRef.current || listLoading) return;
        const cursor = chatCursorRef.current;
        if (!cursor?.time || cursor.id == null) return;

        loadingMoreChatsRef.current = true;
        setLoadingMoreChats(true);
        try {
            const response = await apiCall({
                url: buildConversationListUrl({
                    before_time: cursor.time,
                    before_id: cursor.id,
                }),
                method: "GET",
            });
            if (!response?.success) return;
            const conversations = response.data?.conversations || [];
            if (!conversations.length) {
                hasMoreChatsRef.current = false;
                setHasMoreChats(false);
                return;
            }
            setUsers((prev) => {
                const seen = new Set(prev.map((u) => u.id));
                const unique = conversations.filter((c) => c.id != null && !seen.has(c.id));
                return [...prev, ...unique];
            });
            rememberChatCursor(response.data, conversations);
            if (response.data?.total != null) {
                const n = Number(response.data.total);
                if (Number.isFinite(n)) setTotalParticipants(n);
            }
        } catch (error) {
            console.error("Error loading more chats:", error);
        } finally {
            loadingMoreChatsRef.current = false;
            setLoadingMoreChats(false);
        }
    };

    const onChatListScroll = () => {
        const el = chatListRef.current;
        if (!el) return;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        if (nearBottom) loadMoreChats();
    };

    useEffect(() => {
        const onUnread = (e) => {
            const n = Number(e?.detail?.unreadCount);
            if (Number.isFinite(n)) setInboxUnreadTotal(n);
        };
        window.addEventListener("inbox-unread-changed", onUnread);
        return () => window.removeEventListener("inbox-unread-changed", onUnread);
    }, []);

    useEffect(() => {
        applyLocalFilter(searchTerm, selectedInstance, users, chatKindFilter, channelFilter);
    }, [users, selectedInstance, chatKindFilter, channelFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            getConversationList();
        }, searchTerm.trim() ? 300 : 0);
        return () => clearTimeout(timer);
        // Reload the first page when list filters or search change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstance, channelFilter, chatKindFilter, searchTerm]);

    const scrollToSearchTarget = (msgs, { focusQuery, focusMessageId } = {}) => {
        const q = String(focusQuery || "").trim().toLowerCase();
        let targetId = focusMessageId != null ? String(focusMessageId) : null;

        if (!targetId && q) {
            const hit = [...msgs].reverse().find((m) =>
                String(m.text || "").toLowerCase().includes(q)
            );
            if (hit?.id != null) targetId = String(hit.id);
        }

        if (targetId || focusQuery) {
            setSearchFocus({
                query: focusQuery || "",
                messageId: targetId,
            });
        }

        requestAnimationFrame(() => {
            setTimeout(() => {
                if (targetId) {
                    const el = document.querySelector(`[data-message-id="${targetId}"]`);
                    if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                        el.classList.add("wa-msg-search-flash");
                        setTimeout(() => el.classList.remove("wa-msg-search-flash"), 2500);
                        clearSearchFocusSoon();
                        return;
                    }
                }
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
                if (q) clearSearchFocusSoon();
            }, 120);
        });
    };

    const getConversationMessages = async (chatId, focusOpts = {}) => {
        setMessagesLoading(true);
        setMessages([]);
        setHasMoreMessages(false);
        try {
            const params = new URLSearchParams({
                chat_id: String(chatId),
                limit: String(MESSAGE_PAGE_SIZE),
            });
            if (focusOpts.focusMessageId != null) {
                params.set("around_id", String(focusOpts.focusMessageId));
            }
            const response = await apiCall({
                url: `social/get-conversation-messages?${params.toString()}`,
                method: "GET",
            });
            if (!response?.success) return;

            const payload = response.data;
            const msgs = Array.isArray(payload) ? payload : payload?.messages || [];
            setMessages(msgs);
            setHasMoreMessages(Boolean(payload?.has_more));

            if (payload?.conversation) {
                const conversation = payload.conversation;
                setSelectedUser((prev) =>
                    prev?.id === conversation.id ? { ...prev, ...conversation } : prev
                );
                setUsers((prev) => mergeConversationIntoList(prev, conversation));
            }

            if (focusOpts.focusQuery || focusOpts.focusMessageId != null) {
                stickToBottomRef.current = false;
                scrollToSearchTarget(msgs, focusOpts);
            } else {
                stickToBottomRef.current = true;
                requestAnimationFrame(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
                });
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
        } finally {
            setMessagesLoading(false);
        }
    };

    const loadOlderMessages = async () => {
        if (
            !selectedUser?.id ||
            !hasMoreMessages ||
            loadingOlderRef.current ||
            messagesLoading
        ) {
            return;
        }
        const oldestId = messages[0]?.id;
        if (oldestId == null) return;

        loadingOlderRef.current = true;
        setLoadingOlder(true);
        stickToBottomRef.current = false;
        const container = messagesContainerRef.current;
        const prevHeight = container?.scrollHeight || 0;

        try {
            const params = new URLSearchParams({
                chat_id: String(selectedUser.id),
                limit: String(MESSAGE_PAGE_SIZE),
                before_id: String(oldestId),
            });
            const response = await apiCall({
                url: `social/get-conversation-messages?${params.toString()}`,
                method: "GET",
            });
            if (!response?.success) return;

            const payload = response.data;
            const older = Array.isArray(payload) ? payload : payload?.messages || [];
            if (!older.length) {
                setHasMoreMessages(false);
                return;
            }

            setMessages((prev) => {
                const seen = new Set(prev.map((m) => m.id));
                const unique = older.filter((m) => m.id != null && !seen.has(m.id));
                return [...unique, ...prev];
            });
            setHasMoreMessages(Boolean(payload?.has_more));

            requestAnimationFrame(() => {
                if (container) {
                    container.scrollTop = container.scrollHeight - prevHeight;
                }
            });
        } catch (error) {
            console.error("Error loading older messages:", error);
        } finally {
            loadingOlderRef.current = false;
            setLoadingOlder(false);
        }
    };

    const onMessagesScroll = () => {
        const el = messagesContainerRef.current;
        if (!el) return;
        if (el.scrollTop <= 48) {
            loadOlderMessages();
        }
    };

    // Format recording duration as MM:SS
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
    }

    // Start recording
    const handleStartRecording = () => {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            navigator.mediaDevices
                .getUserMedia({ audio: true })
                .then((stream) => {
                    const preferredTypes = [
                        "audio/webm;codecs=opus",
                        "audio/webm",
                        "audio/ogg;codecs=opus",
                    ];
                    const mimeType = preferredTypes.find((t) => MediaRecorder.isTypeSupported(t)) || "";
                    mediaRecorderRef.current = mimeType
                        ? new MediaRecorder(stream, { mimeType })
                        : new MediaRecorder(stream);
                    audioChunks.current = [];

                    mediaRecorderRef.current.ondataavailable = (e) => {
                        audioChunks.current.push(e.data);
                    };

                    mediaRecorderRef.current.onstop = () => {
                        const recordedType = mediaRecorderRef.current.mimeType || "audio/webm";
                        const audioBlobData = new Blob(audioChunks.current, { type: recordedType });
                        setAudioBlob(audioBlobData);
                    };

                    mediaRecorderRef.current.start()
                    setIsRecording(true)

                    // Start timer
                    setRecordingDuration(0)
                    timerIntervalRef.current = setInterval(() => {
                        setRecordingDuration((prev) => prev + 1)
                    }, 1000)
                })
                .catch((error) => {
                    console.error("Error accessing microphone: ", error)
                })
        }
    }

    // Stop recording
    const handleStopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop()

            // Stop all tracks in the stream
            if (mediaRecorderRef.current.stream) {
                mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop())
            }

            // Clear timer
            clearInterval(timerIntervalRef.current)
            setIsRecording(false)
        }
    }

    // Cancel recording
    const handleCancelRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop()

            // Stop all tracks in the stream
            if (mediaRecorderRef.current.stream) {
                mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop())
            }

            // Clear timer and reset states
            clearInterval(timerIntervalRef.current)
            setIsRecording(false)
            setAudioBlob(null)
            audioChunks.current = []
        }
    }

    // Send recorded audio
    const handleSendRecording = () => {
        if (audioBlob) {
            handleSendMessage("audio")
        }
    }

    const closeChat = () => {
        setSelectedUser(null);
        setMessages([]);
        setMenuOpen(false);
        setContactInfoOpen(false);
        sessionStorage.removeItem(SELECTED_CHAT_KEY);
        setSidebarOpen(true);
    };

    const refreshProfilePhoto = async () => {
        if (!selectedUser?.id) return;
        setMenuLoading(true);
        try {
            const response = await apiCall({
                url: "social/refresh-profile-photo",
                method: "POST",
                body: {
                    chat_id: selectedUser.id,
                    client_id: resolveSendClientId(),
                },
            });
            if (!response.success) {
                showToast(response.msg || "Could not refresh profile photo", "danger");
                return;
            }
            const photo = response.data?.profile_photo;
            const photoUrl = response.data?.profile_photo_url || photo;
            const patch = {
                profile_photo: photo,
                profile_photo_url: photoUrl,
            };
            setSelectedUser((prev) => (prev ? { ...prev, ...patch } : prev));
            setUsers((prev) =>
                prev.map((u) => (u.id === selectedUser.id ? { ...u, ...patch } : u))
            );
        } catch (error) {
            showToast(error.message || "Could not refresh profile photo", "danger");
        } finally {
            setMenuLoading(false);
            setMenuOpen(false);
        }
    };

    const clearChatMessages = async () => {
        if (!selectedUser?.id) return;
        if (!window.confirm(`Clear all messages in "${selectedUser.name}"?`)) return;
        setMenuLoading(true);
        try {
            const response = await apiCall({
                url: "social/clear-conversation",
                method: "POST",
                body: { chat_id: selectedUser.id },
            });
            if (!response.success) {
                showToast(response.msg || "Could not clear chat", "danger");
                return;
            }
            setMessages([]);
            bumpConversation(selectedUser, {
                lastMessage: "",
                lastMessageTime: new Date(),
                unreadCount: 0,
            });
        } catch (error) {
            showToast(error.message || "Could not clear chat", "danger");
        } finally {
            setMenuLoading(false);
            setMenuOpen(false);
        }
    };

    const deleteChat = async () => {
        if (!selectedUser?.id) return;
        if (!window.confirm(`Delete chat with "${selectedUser.name}"? This cannot be undone.`)) return;
        setMenuLoading(true);
        try {
            const response = await apiCall({
                url: "social/delete-conversation",
                method: "POST",
                body: { chat_id: selectedUser.id },
            });
            if (!response.success) {
                showToast(response.msg || "Could not delete chat", "danger");
                return;
            }
            const deletedId = selectedUser.id;
            setUsers((prev) => prev.filter((u) => u.id !== deletedId));
            closeChat();
        } catch (error) {
            showToast(error.message || "Could not delete chat", "danger");
        } finally {
            setMenuLoading(false);
            setMenuOpen(false);
        }
    };

    useEffect(() => {
        if (!menuOpen) return;
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [menuOpen]);

    const applyInstanceFilter = (sourceUsers, instance, { whatsappOnly = false } = {}) => {
        if (!instance || instance === "all") return sourceUsers;
        const want = String(instance);
        return sourceUsers.filter((user) => {
            const channel = getChannelType(user);
            // Meta chats are not tied to WA instances — keep them when mixing channels
            if (whatsappOnly && (channel === "facebook" || channel === "instagram")) {
                return true;
            }
            const id = getConversationInstanceId(user);
            if (!id) return false;
            return id === want;
        });
    };

    const applyKindFilter = (sourceUsers, kind = chatKindFilter) => {
        if (kind === "groups") return sourceUsers.filter(isGroupConversation);
        if (kind === "direct") return sourceUsers.filter((u) => !isGroupConversation(u));
        return sourceUsers;
    };

    const getChannelType = (user) => {
        const t = String(user?.type || "whatsapp").toLowerCase().trim();
        if (t === "facebook" || t === "instagram" || t === "group") return t;
        return "whatsapp";
    };

    const applyChannelFilter = (sourceUsers, channel = channelFilter) => {
        if (!channel || channel === "all") return sourceUsers;
        if (channel === "whatsapp") {
            return sourceUsers.filter((u) => {
                const t = getChannelType(u);
                return t === "whatsapp" || t === "group";
            });
        }
        return sourceUsers.filter((u) => getChannelType(u) === channel);
    };

    const applyLocalFilter = (
        term,
        instance,
        sourceUsers = users,
        kind = chatKindFilter,
        channel = channelFilter
    ) => {
        let list = Array.isArray(sourceUsers) ? sourceUsers : [];
        list = applyChannelFilter(list, channel);

        // Instance dropdown only constrains WhatsApp. On "All", keep Meta chats visible
        // and only narrow WhatsApp rows when a specific instance is selected.
        if (instance && instance !== "all") {
            if (channel === "whatsapp") {
                list = applyInstanceFilter(list, instance);
            } else if (channel === "all") {
                list = applyInstanceFilter(list, instance, { whatsappOnly: true });
            }
        }

        list = applyKindFilter(list, kind);

        if (term.trim()) {
            const t = term.toLowerCase();
            const digits = term.replace(/\D/g, "");
            list = list.filter(
                (user) =>
                    user.name?.toLowerCase().includes(t) ||
                    user.sid?.toLowerCase?.().includes(t) ||
                    user.wa_jid?.toLowerCase?.().includes(t) ||
                    (digits && user.sid?.includes(digits)) ||
                    user.lastMessage?.toLowerCase().includes(t)
            );
        }

        setFilteredUsers(list);
        return list;
    };

    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        applyLocalFilter(term, selectedInstance);
    };

    const startChatWithContact = async (contact, { isGroup = false } = {}) => {
        const clientId = selectedInstance !== "all" ? selectedInstance : null;
        if (!clientId) {
            showToast("Select an active WhatsApp instance to start a chat", "danger");
            return;
        }

        const jid = contact.jid || contact.id || contact.wa_jid;
        const group = isGroup || isGroupJid(jid) || contact.is_group;

        try {
            const existing = users.find(
                (u) =>
                    (u.sid === contact.sid ||
                        u.wa_jid === jid ||
                        u.sid === jid ||
                        normMatchJid(u, jid)) &&
                    getConversationInstanceId(u) === clientId
            );
            if (existing) {
                selectUser(existing);
                setSearchTerm("");
                setContactResults([]);
                setGroupResults([]);
                setPhoneSuggestion(null);
                setSidebarOpen(false);
                return;
            }

            const response = await apiCall({
                url: "social/start-conversation",
                method: "POST",
                body: {
                    jid,
                    phone: group ? undefined : contact.phone,
                    name: contact.name || contact.subject || jid,
                    client_id: clientId,
                    is_group: !!group,
                    chat_type: group ? "group" : "direct",
                },
            });

            if (!response.success) {
                showToast(response.msg || "Could not start chat", "danger");
                return;
            }

            const conv = {
                ...response.data,
                type: group ? "group" : "whatsapp",
                is_group: group || response.data.is_group || isGroupJid(response.data.wa_jid || response.data.sid),
                chat_type: group ? "group" : response.data.chat_type || "direct",
                wa_jid: response.data.wa_jid || jid,
                client_id: response.data.client_id || clientId,
                unreadCount: response.data.unreadCount || 0,
            };

            setUsers((prev) => [conv, ...prev.filter((u) => u.id !== conv.id)]);
            applyLocalFilter("", selectedInstance, [conv, ...users.filter((u) => u.id !== conv.id)]);
            setContactResults([]);
            setGroupResults([]);
            setPhoneSuggestion(null);
            setSearchTerm("");
            selectUser(conv);
            setSidebarOpen(false);
        } catch (error) {
            showToast(error.message || "Could not start chat", "danger");
        }
    };

    const normMatchJid = (user, jid) =>
        String(user?.wa_jid || user?.sid || "").toLowerCase() === String(jid || "").toLowerCase();

    const filterByInstance = (val) => {
        setSelectedInstance(val);
        sessionStorage.setItem(SELECTED_INSTANCE_KEY, val);
        const filtered = applyLocalFilter(searchTerm, val, users, chatKindFilter, channelFilter);
        if (selectedUser && !filtered.some((u) => u.id === selectedUser.id)) {
            setSelectedUser(null);
            setMessages([]);
            setHasMoreMessages(false);
            sessionStorage.removeItem(SELECTED_CHAT_KEY);
        }
    };

    useEffect(() => {
        const q = searchTerm.trim();
        if (q.length < 2) {
            setMessageSearchResults([]);
            setMessageSearchLoading(false);
            return undefined;
        }

        const timer = setTimeout(async () => {
            setMessageSearchLoading(true);
            try {
                const res = await apiCall({
                    url: `social/search-messages?q=${encodeURIComponent(q)}&limit=40`,
                    method: "GET",
                });
                setMessageSearchResults(res?.success ? res.data || [] : []);
            } catch (error) {
                console.error("Message search failed:", error);
                setMessageSearchResults([]);
            } finally {
                setMessageSearchLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (!searchTerm.trim() || selectedInstance === "all") {
            setContactResults([]);
            setGroupResults([]);
            setPhoneSuggestion(null);
            return;
        }

        const timer = setTimeout(async () => {
            setSearchLoading(true);
            const q = searchTerm.trim().toLowerCase();
            try {
                const [contactsRes, groupsRes] = await Promise.all([
                    apiCall({
                        url: `social/search-whatsapp-contacts?q=${encodeURIComponent(searchTerm.trim())}&client_id=${encodeURIComponent(selectedInstance)}`,
                        method: "GET",
                    }).catch(() => null),
                    apiCall({
                        url: `groups?id=${encodeURIComponent(selectedInstance)}`,
                        method: "GET",
                    }).catch(() => null),
                ]);

                if (contactsRes?.success) {
                    setContactResults(contactsRes.data.contacts || []);
                    setPhoneSuggestion(contactsRes.data.phoneSuggestion || null);
                } else {
                    setContactResults([]);
                    setPhoneSuggestion(null);
                }

                const groups = Array.isArray(groupsRes?.data)
                    ? groupsRes.data
                    : Array.isArray(groupsRes?.groups)
                      ? groupsRes.groups
                      : [];
                const matched = groups
                    .filter((g) => {
                        const name = String(g.name || g.subject || "").toLowerCase();
                        const id = String(g.id || g.jid || "").toLowerCase();
                        return name.includes(q) || id.includes(q);
                    })
                    .slice(0, 20)
                    .map((g) => ({
                        id: g.id || g.jid,
                        jid: g.id || g.jid,
                        name: g.name || g.subject || g.id,
                        is_group: true,
                        participants: g.participants || g.size || g.participant_count,
                    }));
                setGroupResults(matched);
            } catch (error) {
                console.error("Contact/group search failed:", error);
            } finally {
                setSearchLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [searchTerm, selectedInstance]);

    const openMessageSearchHit = (hit) => {
        if (!hit?.id && !hit?.chat_id) return;
        const chatId = hit.chat_id || hit.id;
        const existing = users.find((u) => String(u.id) === String(chatId));
        const conv = existing || {
            id: chatId,
            name: hit.chat_name || hit.name || "Chat",
            type: hit.chat_type || hit.type || "whatsapp",
            sid: hit.sid,
            wa_jid: hit.wa_jid,
            client_id: hit.client_id,
            profile_photo: hit.profile_photo,
            profile_photo_url: hit.profile_photo_url,
            is_group: hit.is_group,
            lastMessage: hit.preview || hit.text,
            lastMessageTime: hit.timestamp,
            unreadCount: 0,
        };
        if (!existing) {
            setUsers((prev) => [conv, ...prev.filter((u) => String(u.id) !== String(chatId))]);
        }
        const q = searchTerm.trim();
        setSearchTerm("");
        setMessageSearchResults([]);
        setContactResults([]);
        setGroupResults([]);
        setPhoneSuggestion(null);
        selectUser(conv, {
            focusQuery: q,
            focusMessageId: hit.message_id ?? null,
        });
        setSidebarOpen(false);
    };

    const openChatFromSearchList = (user) => {
        const q = searchTerm.trim();
        setSearchTerm("");
        setMessageSearchResults([]);
        setContactResults([]);
        setGroupResults([]);
        setPhoneSuggestion(null);
        selectUser(user, q ? { focusQuery: q } : {});
        setSidebarOpen(false);
    };
    // Use a ref to store the latest selectedUser value
    const selectedUserRef = useRef(selectedUser);
    const usersRef = useRef(users);

    useEffect(() => {
        selectedUserRef.current = selectedUser;
        usersRef.current = users;
    }, [selectedUser, users]);

    useEffect(() => {
        document.title = "WhatsApp Chat";

        fetchInstances();
        apiCall({ url: "features/quick-replies", method: "GET" })
            .then((res) => {
                if (res?.success) setQuickReplies(res.data || []);
            })
            .catch(() => {});

        const wsBase = getWsBaseUrl();
        let socket;
        let pingInterval;
        let reconnectTimer;
        let isUnmounted = false;

        const connectWebSocket = () => {
            socket = new WebSocket(wsBase);

            socket.onopen = () => {
                const token = localStorage.getItem("token");
                if (token) {
                    socket.send(JSON.stringify({ type: "auth", token }));
                }

                pingInterval = setInterval(() => {
                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({ type: "ping" }));
                    }
                }, 30000);
            };

            socket.onmessage = (event) => {
                let eventData;
                try {
                    eventData = JSON.parse(event.data);
                } catch {
                    return;
                }

                // Ignore keepalive / auth / logs events on chat page
                if (["pong", "auth_ok", "auth_error", "log_stats", "message_log"].includes(eventData.type)) {
                    return;
                }

                if (eventData.type === "conversation_updated") {
                    const photoPatch = {
                        profile_photo: eventData.profile_photo,
                        profile_photo_url: eventData.profile_photo_url
                            ? mediaUrl(eventData.profile_photo_url)
                            : eventData.profile_photo
                              ? mediaUrl(eventData.profile_photo)
                              : null,
                    };

                    setUsers((list) =>
                        list.map((u) => (sameChat(u, eventData) ? { ...u, ...photoPatch } : u))
                    );

                    if (sameChat(selectedUserRef.current, eventData)) {
                        setSelectedUser((prev) => (prev ? { ...prev, ...photoPatch } : prev));
                    }
                    return;
                }

                if (eventData.type === "message_reaction") {
                    const mid = eventData.message_id;
                    const reactions = Array.isArray(eventData.reactions) ? eventData.reactions : [];
                    if (mid != null) {
                        setMessages((prev) =>
                            prev.map((m) =>
                                String(m.id) === String(mid) ? { ...m, reactions } : m
                            )
                        );
                    }
                    return;
                }

                if (eventData.type === "message_status") {
                    const mid = eventData.message_id;
                    const waId = eventData.wa_message_id;
                    const delivery = eventData.delivery_status;
                    if (!delivery) return;
                    setMessages((prev) =>
                        prev.map((m) => {
                            const match =
                                (mid != null && String(m.id) === String(mid)) ||
                                (waId && m.wa_message_id && String(m.wa_message_id) === String(waId));
                            if (!match) return m;
                            const order = { sent: 1, delivered: 2, read: 3 };
                            const cur = order[m.delivery_status] || 0;
                            const next = order[delivery] || 0;
                            if (next < cur) return m;
                            return { ...m, delivery_status: delivery };
                        })
                    );
                    return;
                }

                if (eventData.type === "unread_count") {
                    if (Number.isFinite(Number(eventData.unreadCount))) {
                        publishInboxUnread(Number(eventData.unreadCount) || 0);
                    }
                    return;
                }

                if (eventData.type === "typing") {
                    const chatKey = eventData.chat_id != null ? String(eventData.chat_id) : null;
                    if (!chatKey) return;
                    setTypingByChat((prev) => ({
                        ...prev,
                        [chatKey]: {
                            is_typing: Boolean(eventData.is_typing),
                            is_recording: Boolean(eventData.is_recording),
                            typer_name: eventData.typer_name || null,
                        },
                    }));
                    return;
                }

                if (eventData.type === "incoming_call") {
                    if (eventData.is_ringing !== false) {
                        setIncomingCall(eventData);
                        playInboxChime();
                    }
                    return;
                }

                if (eventData.type === "call_update") {
                    setIncomingCall((prev) =>
                        prev && String(prev.call_id) === String(eventData.call_id) ? null : prev
                    );
                    return;
                }

                const isChatPayload =
                    eventData.type === "chat_message" ||
                    (eventData.text != null && (eventData.chat_id != null || eventData.sid));

                if (!isChatPayload) {
                    return;
                }

                const sUser = selectedUserRef.current;
                const isActiveChat = sameChat(sUser, eventData);
                const known = usersRef.current.some((u) => sameChat(u, eventData));

                if (!known) {
                    getConversationList({ silent: true, merge: true });
                }

                if (isActiveChat) {
                    appendMessage(eventData);
                }

                // Peer sent a message → clear typing indicator for that chat
                if (eventData.sender === "other" && eventData.chat_id != null) {
                    setTypingByChat((prev) => ({
                        ...prev,
                        [String(eventData.chat_id)]: {
                            is_typing: false,
                            is_recording: false,
                            typer_name: null,
                        },
                    }));
                }

                if (eventData.sender === "other") {
                    playInboxChime();
                }

                const existing = usersRef.current.find((u) => sameChat(u, eventData));
                let unreadCount = existing?.unreadCount || 0;
                if (isActiveChat) {
                    unreadCount = 0;
                } else if (eventData.sender === "other" && !eventData.is_read) {
                    unreadCount += 1;
                }

                bumpConversation(eventData, {
                    lastMessage:
                        eventData.preview ||
                        mediaPreviewLabel(eventData.msg_type, eventData.text) ||
                        eventData.text,
                    lastMessageTime: eventData.timestamp || new Date(),
                    unreadCount,
                    ...(eventData.client_id ? { client_id: eventData.client_id } : {}),
                });
            };

            socket.onerror = (error) => {
                console.error("WebSocket error:", error);
            };

            socket.onclose = () => {
                clearInterval(pingInterval);
                if (!isUnmounted) {
                    reconnectTimer = setTimeout(connectWebSocket, 5000);
                }
            };
        };

        connectWebSocket();

        return () => {
            isUnmounted = true;
            clearInterval(pingInterval);
            clearTimeout(reconnectTimer);
            if (socket && socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
            if (isRecording) {
                clearInterval(timerIntervalRef.current);
                if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                    mediaRecorderRef.current.stop();
                    if (mediaRecorderRef.current.stream) {
                        mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
                    }
                }
            }
        };
    }, []);

    useEffect(() => {
        if (!stickToBottomRef.current) return;
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    }, [messages, selectedUser]);

    const showChatOnMobile = selectedUser && !sidebarOpen;
    const showListOnMobile = sidebarOpen || !selectedUser;

    return (
        <div className="wa-chat-app">
            {toast ? (
                <div className={`wa-toast wa-toast-${toast.tone}`} role="status">
                    <Icon
                        icon={
                            toast.tone === "success"
                                ? "mdi:check-circle"
                                : toast.tone === "danger"
                                  ? "mdi:alert-circle"
                                  : "mdi:information"
                        }
                        width={18}
                    />
                    <span>{toast.message}</span>
                    <button type="button" aria-label="Dismiss" onClick={() => setToast(null)}>
                        <Icon icon="mdi:close" width={16} />
                    </button>
                </div>
            ) : null}

            {/* Left — chat list */}
            <aside className={`wa-sidebar ${showListOnMobile ? "" : "hidden-mobile"}`}>
                <div className="wa-sidebar-header">
                    <h5>Chats</h5>
                    <div className="wa-sidebar-header-meta">
                        <span className="wa-sidebar-count" title="Conversations">
                            {totalParticipants ?? users.length ?? 0}
                        </span>
                        {inboxUnreadTotal > 0 && (
                            <span className="wa-sidebar-unread" title="Unread messages">
                                {inboxUnreadTotal > 999 ? "999+" : inboxUnreadTotal}
                            </span>
                        )}
                    </div>
                </div>

                <div className="wa-sidebar-toolbar">
                    <div className="wa-search-wrap">
                        <Icon icon="mdi:magnify" className="wa-search-icon" />
                        <input
                            type="text"
                            placeholder="Search chats or messages…"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                    <div className="wa-channel-filters" role="tablist" aria-label="Channel filter">
                        {[
                            { id: "all", label: "All", icon: "mdi:forum-outline" },
                            { id: "whatsapp", label: "WhatsApp", icon: "mdi:whatsapp" },
                            { id: "facebook", label: "Facebook", icon: "mdi:facebook" },
                            { id: "instagram", label: "Instagram", icon: "mdi:instagram" },
                        ].map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={`wa-channel-filter${channelFilter === item.id ? " active" : ""} ${item.id}`}
                                onClick={() => setChannelFilter(item.id)}
                            >
                                <Icon icon={item.icon} width={15} />
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </div>
                    {(channelFilter === "all" || channelFilter === "whatsapp") && (
                        <select
                            className="wa-filter-select"
                            value={selectedInstance}
                            onChange={(e) => filterByInstance(e.target.value)}
                        >
                            <option value="all">All instances</option>
                            {instances.map((instance) => (
                                <option key={instance.client_id} value={instance.client_id}>
                                    {instance.name || instance.client_id}
                                </option>
                            ))}
                        </select>
                    )}
                    <div className="wa-kind-filters">
                        {[
                            { id: "all", label: "All" },
                            { id: "direct", label: "Chats" },
                            { id: "groups", label: "Groups" },
                        ].map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={`wa-kind-filter${chatKindFilter === item.id ? " active" : ""}`}
                                onClick={() => setChatKindFilter(item.id)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="wa-chat-list" ref={chatListRef} onScroll={onChatListScroll}>
                    {listLoading && !searchTerm.trim() && (
                        <div className="wa-list-loading" role="status" aria-live="polite">
                            <div className="wa-spinner" aria-hidden="true" />
                            <p>Loading chats…</p>
                            <div className="wa-skeleton-list">
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} className="wa-skeleton-row">
                                        <span className="wa-skeleton-avatar" />
                                        <span className="wa-skeleton-lines">
                                            <span className="wa-skeleton-line short" />
                                            <span className="wa-skeleton-line" />
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {searchTerm.trim() && (searchLoading || messageSearchLoading) && (
                        <div className="wa-search-status">
                            <div className="wa-spinner wa-spinner-sm" aria-hidden="true" />
                            Searching…
                        </div>
                    )}

                    {searchTerm.trim().length >= 2 && messageSearchResults.length > 0 && (
                        <>
                            <div className="wa-search-section-label">Messages</div>
                            {messageSearchResults.map((hit) => (
                                <div
                                    key={`msg-${hit.message_id}`}
                                    className="wa-chat-item wa-contact-item"
                                    onClick={() => openMessageSearchHit(hit)}
                                >
                                    <ChatAvatar
                                        user={{
                                            name: hit.name || hit.chat_name,
                                            type: hit.type || hit.chat_type,
                                            profile_photo_url: hit.profile_photo_url,
                                            profile_photo: hit.profile_photo,
                                            is_group: hit.is_group,
                                        }}
                                        size={49}
                                    />
                                    <div className="wa-chat-item-body">
                                        <div className="wa-chat-item-top">
                                            <p className="wa-chat-item-name">
                                                {highlightMatch(hit.name || hit.chat_name || "Chat", searchTerm)}
                                            </p>
                                            <span className="wa-chat-item-time">
                                                {formatDate(hit.timestamp)}
                                            </span>
                                        </div>
                                        <div className="wa-chat-item-bottom">
                                            <p className="wa-chat-item-preview">
                                                {hit.sender === "user" ? "You: " : ""}
                                                {highlightMatch(
                                                    hit.preview || hit.text || mediaPreviewLabel(hit.msg_type),
                                                    searchTerm
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {searchTerm.trim() &&
                        phoneSuggestion &&
                        !users.some((u) => u.sid === phoneSuggestion.sid) && (
                            <>
                                <div className="wa-search-section-label">New chat</div>
                                <div
                                    className="wa-chat-item wa-contact-item"
                                    onClick={() => startChatWithContact(phoneSuggestion)}
                                >
                                    <div className="wa-avatar whatsapp">
                                        <Icon icon="mdi:account-plus" width={22} />
                                    </div>
                                    <div className="wa-chat-item-body">
                                        <p className="wa-chat-item-name">
                                            Message +{phoneSuggestion.phone}
                                        </p>
                                        <p className="wa-chat-item-preview">Start WhatsApp chat</p>
                                    </div>
                                </div>
                            </>
                        )}

                    {searchTerm.trim() && contactResults.length > 0 && (
                        <>
                            <div className="wa-search-section-label">WhatsApp contacts</div>
                            {contactResults.map((contact) => (
                                <div
                                    key={contact.jid}
                                    className="wa-chat-item wa-contact-item"
                                    onClick={() => startChatWithContact(contact)}
                                >
                                    <ChatAvatar
                                        user={{
                                            name: contact.name,
                                            type: "whatsapp",
                                            profile_photo_url: null,
                                        }}
                                        size={49}
                                    />
                                    <div className="wa-chat-item-body">
                                        <p className="wa-chat-item-name">{contact.name}</p>
                                        <p className="wa-chat-item-preview">+{contact.phone}</p>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {searchTerm.trim() && groupResults.length > 0 && (
                        <>
                            <div className="wa-search-section-label">WhatsApp groups</div>
                            {groupResults.map((group) => (
                                <div
                                    key={group.jid || group.id}
                                    className="wa-chat-item wa-contact-item"
                                    onClick={() => startChatWithContact(group, { isGroup: true })}
                                >
                                    <ChatAvatar
                                        user={{
                                            name: group.name,
                                            is_group: true,
                                            type: "group",
                                        }}
                                        size={49}
                                    />
                                    <div className="wa-chat-item-body">
                                        <p className="wa-chat-item-name">
                                            {group.name}
                                            <span className="wa-group-badge">Group</span>
                                        </p>
                                        <p className="wa-chat-item-preview">
                                            {group.participants
                                                ? `${group.participants} participants`
                                                : group.jid}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {searchTerm.trim() && filteredUsers.length > 0 && (
                        <div className="wa-search-section-label">Chats</div>
                    )}

                    {[...filteredUsers]
                        .sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime))
                        .map((user) => {
                            const group = isGroupConversation(user);
                            return (
                            <div
                                key={user.id}
                                className={`wa-chat-item ${selectedUser?.id === user.id ? "active" : ""}`}
                                onClick={() => openChatFromSearchList(user)}
                            >
                                <ChatAvatar user={user} size={49} />
                                <div className="wa-chat-item-body">
                                    <div className="wa-chat-item-top">
                                        <p className="wa-chat-item-name">
                                            {searchTerm.trim()
                                                ? highlightMatch(user.name, searchTerm)
                                                : user.name}
                                            {group && <span className="wa-group-badge">Group</span>}
                                        </p>
                                        <span className="wa-chat-item-time">
                                            {formatDate(user.lastMessageTime)}
                                        </span>
                                    </div>
                                    <div className="wa-chat-item-bottom">
                                        <p className="wa-chat-item-preview">
                                            {typingByChat[String(user.id)]?.is_typing ? (
                                                <span className="wa-typing-preview">
                                                    {typingByChat[String(user.id)]?.is_recording
                                                        ? "recording…"
                                                        : "typing…"}
                                                </span>
                                            ) : searchTerm.trim() ? (
                                                highlightMatch(user.lastMessage || "No messages yet", searchTerm)
                                            ) : (
                                                user.lastMessage || "No messages yet"
                                            )}
                                        </p>
                                        {Number(user.unreadCount) > 0 && (
                                            <span className="wa-unread-badge">
                                                {Number(user.unreadCount) > 99
                                                    ? "99+"
                                                    : Number(user.unreadCount)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            );
                        })}

                    {searchTerm.trim() &&
                        !searchLoading &&
                        !messageSearchLoading &&
                        filteredUsers.length === 0 &&
                        messageSearchResults.length === 0 &&
                        contactResults.length === 0 &&
                        groupResults.length === 0 &&
                        !phoneSuggestion && (
                            <div className="wa-search-status">No chats, messages, contacts, or groups found</div>
                        )}

                    {loadingMoreChats && (
                        <div className="wa-search-status">Loading more chats…</div>
                    )}

                    {!listLoading && !searchTerm.trim() && filteredUsers.length === 0 && (
                        <div className="wa-empty-channel">
                            {channelFilter === "facebook" ? (
                                <>
                                    <Icon icon="mdi:facebook" width={28} />
                                    <p>No Facebook chats yet</p>
                                    <span>
                                        Go to <strong>Webhooks</strong> → Sync Facebook chats.
                                        If sync finds none, someone must message your Page on Messenger first.
                                    </span>
                                </>
                            ) : channelFilter === "instagram" ? (
                                <>
                                    <Icon icon="mdi:instagram" width={28} />
                                    <p>No Instagram chats yet</p>
                                    <span>
                                        Connect Instagram in Webhooks, then sync. IG must be linked to your Facebook Page.
                                    </span>
                                </>
                            ) : channelFilter === "whatsapp" ? (
                                <>
                                    <Icon icon="mdi:whatsapp" width={28} />
                                    <p>No WhatsApp chats for this filter</p>
                                    <span>Try another instance, or start a new chat from search.</span>
                                </>
                            ) : (
                                <>
                                    <Icon icon="mdi:chat-outline" width={28} />
                                    <p>No chats found</p>
                                    <span>Use the channel filter above to switch WhatsApp / Facebook / Instagram.</span>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </aside>

            {/* Right — conversation */}
            <section className={`wa-main ${showChatOnMobile || !showListOnMobile ? "" : "hidden-mobile"}`}>
                {!selectedUser ? (
                    <div className="wa-main-empty">
                        <Icon icon="mdi:whatsapp" className="wa-main-empty-icon" />
                        <h4>WhatsApp Web</h4>
                        <p>
                            Keep conversations in one place. Pick a chat from the left
                            to start messaging with your selected instance.
                        </p>
                    </div>
                ) : (
                    <>
                        <header className="wa-chat-header">
                            <div className="wa-chat-header-left">
                                <button
                                    type="button"
                                    className="wa-back-btn"
                                    onClick={() => setSidebarOpen(true)}
                                >
                                    <Icon icon="mdi:arrow-left" />
                                </button>
                                <ChatAvatar
                                    user={selectedUser}
                                    size={40}
                                    onClick={() => setContactInfoOpen(true)}
                                />
                                <button
                                    type="button"
                                    className="wa-chat-header-info wa-chat-header-info-btn"
                                    onClick={() => setContactInfoOpen(true)}
                                >
                                    <h6>
                                        {selectedUser.name}
                                        {isGroupConversation(selectedUser) && (
                                            <span className="wa-group-badge">Group</span>
                                        )}
                                    </h6>
                                    <small>
                                        {(() => {
                                            const typing = typingByChat[String(selectedUser.id)];
                                            if (typing?.is_typing) {
                                                if (typing.is_recording) {
                                                    return (
                                                        <span className="wa-typing-header">recording…</span>
                                                    );
                                                }
                                                if (isGroupConversation(selectedUser) && typing.typer_name) {
                                                    return (
                                                        <span className="wa-typing-header">
                                                            {String(typing.typer_name).replace(/@.*$/, "")} typing…
                                                        </span>
                                                    );
                                                }
                                                return <span className="wa-typing-header">typing…</span>;
                                            }
                                            if (isGroupConversation(selectedUser)) {
                                                const count =
                                                    selectedUser.participant_count ||
                                                    selectedUser.participants?.length;
                                                return count
                                                    ? `${count} participants`
                                                    : "Group chat";
                                            }
                                            const t = String(selectedUser.type || "whatsapp").toLowerCase();
                                            if (t === "facebook") return "Facebook Messenger";
                                            if (t === "instagram") return "Instagram";
                                            const id = resolveSendClientId();
                                            const inst = instances.find((i) => i.client_id === id);
                                            return inst
                                                ? `WhatsApp · ${inst.name || id}`
                                                : "WhatsApp";
                                        })()}
                                    </small>
                                </button>
                            </div>
                            <div className="wa-chat-header-actions" ref={menuRef}>
                                {!isGroupConversation(selectedUser) &&
                                    String(selectedUser.type || "whatsapp").toLowerCase() === "whatsapp" && (
                                        <>
                                            <button
                                                type="button"
                                                aria-label="Voice call on WhatsApp"
                                                title="Voice call (opens WhatsApp)"
                                                onClick={() => openWhatsAppCall("voice")}
                                            >
                                                <Icon icon="mdi:phone-outline" />
                                            </button>
                                            <button
                                                type="button"
                                                aria-label="Video call on WhatsApp"
                                                title="Video call (opens WhatsApp)"
                                                onClick={() => openWhatsAppCall("video")}
                                            >
                                                <Icon icon="mdi:video-outline" />
                                            </button>
                                        </>
                                    )}
                                <button
                                    type="button"
                                    aria-label="Search"
                                    onClick={() => {
                                        const el = document.querySelector(".wa-search-wrap input");
                                        el?.focus();
                                        setSidebarOpen(true);
                                    }}
                                >
                                    <Icon icon="mdi:magnify" />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Menu"
                                    aria-expanded={menuOpen}
                                    onClick={() => setMenuOpen((open) => !open)}
                                    disabled={menuLoading}
                                >
                                    <Icon icon="mdi:dots-vertical" />
                                </button>
                                {menuOpen && (
                                    <div className="wa-chat-menu">
                                        <button type="button" onClick={() => { setContactInfoOpen(true); setMenuOpen(false); }}>
                                            <Icon icon={isGroupConversation(selectedUser) ? "mdi:account-group-outline" : "mdi:account-circle-outline"} />
                                            {isGroupConversation(selectedUser) ? "Group info" : "Contact info"}
                                        </button>
                                        <button type="button" onClick={refreshProfilePhoto} disabled={menuLoading}>
                                            <Icon icon="mdi:image-refresh-outline" />
                                            Refresh photo
                                        </button>
                                        <button type="button" onClick={clearChatMessages} disabled={menuLoading}>
                                            <Icon icon="mdi:notification-clear-all" />
                                            Clear messages
                                        </button>
                                        <button type="button" className="danger" onClick={deleteChat} disabled={menuLoading}>
                                            <Icon icon="mdi:delete-outline" />
                                            Delete chat
                                        </button>
                                        <button type="button" onClick={closeChat}>
                                            <Icon icon="mdi:close" />
                                            Close chat
                                        </button>
                                    </div>
                                )}
                            </div>
                        </header>

                        {contactInfoOpen && (
                            <div className="wa-contact-modal-backdrop" onClick={() => setContactInfoOpen(false)}>
                                <div className="wa-contact-modal" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        className="wa-contact-modal-close"
                                        onClick={() => setContactInfoOpen(false)}
                                        aria-label="Close"
                                    >
                                        <Icon icon="mdi:close" />
                                    </button>
                                    <ChatAvatar user={selectedUser} size={96} />
                                    <h5>{selectedUser.name}</h5>
                                    <p className="wa-contact-modal-meta">
                                        {isGroupConversation(selectedUser)
                                            ? "WhatsApp group"
                                            : String(selectedUser.type || "").toLowerCase() === "facebook"
                                              ? "Facebook Messenger"
                                              : String(selectedUser.type || "").toLowerCase() === "instagram"
                                                ? "Instagram"
                                                : "WhatsApp"}
                                        {selectedUser.sid && isMetaChannel(selectedUser)
                                            ? ` · ${selectedUser.sid}`
                                            : ""}
                                    </p>
                                    {!isMetaChannel(selectedUser) &&
                                        (selectedUser.wa_jid ||
                                            (isGroupConversation(selectedUser) && selectedUser.sid)) && (
                                        <p className="wa-contact-modal-meta">
                                            JID: {selectedUser.wa_jid || selectedUser.sid}
                                        </p>
                                    )}
                                    {isGroupConversation(selectedUser) &&
                                        (selectedUser.participant_count ||
                                            selectedUser.participants?.length) && (
                                            <p className="wa-contact-modal-meta">
                                                Participants:{" "}
                                                {selectedUser.participant_count ||
                                                    selectedUser.participants.length}
                                            </p>
                                        )}
                                    {!isMetaChannel(selectedUser) &&
                                        (() => {
                                            const id = resolveSendClientId();
                                            const inst = instances.find((i) => i.client_id === id);
                                            return inst ? (
                                                <p className="wa-contact-modal-meta">
                                                    Instance: {inst.name || id}
                                                </p>
                                            ) : null;
                                        })()}
                                    <div className="wa-contact-modal-actions">
                                        <button type="button" className="wa-contact-action" onClick={refreshProfilePhoto}>
                                            Refresh photo
                                        </button>
                                        <button type="button" className="wa-contact-action" onClick={clearChatMessages}>
                                            Clear messages
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div
                            className="wa-messages"
                            ref={messagesContainerRef}
                            onScroll={onMessagesScroll}
                        >
                            {loadingOlder && (
                                <div className="wa-messages-loading wa-messages-loading-top" role="status">
                                    <div className="wa-spinner" aria-hidden="true" />
                                    <span>Loading older…</span>
                                </div>
                            )}
                            {messagesLoading && messages.length === 0 && (
                                <div className="wa-messages-loading" role="status">
                                    <div className="wa-spinner" aria-hidden="true" />
                                    <span>Loading messages…</span>
                                </div>
                            )}
                            {reactPicker && (
                                <div
                                    className="wa-react-popover"
                                    style={{ left: reactPicker.x, top: reactPicker.y }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    {QUICK_REACTS.map((emoji) => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            className="wa-react-popover-item"
                                            onClick={() => sendReaction(emoji)}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        className="wa-react-popover-item clear"
                                        title="Remove your reaction"
                                        onClick={() => sendReaction("")}
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}
                            {messages.map((message, index) => {
                                const isOut = message.sender === "user";
                                const groupChat = isGroupConversation(selectedUser);
                                const sendStatus = message.send_status || "sent";
                                // Show hover react on every real inbox message (not temp/pending/failed).
                                const canReact =
                                    message.id != null &&
                                    !String(message.id).startsWith("tmp_") &&
                                    sendStatus !== "pending" &&
                                    sendStatus !== "failed";
                                const senderName =
                                    message.sender_name ||
                                    message.from_name ||
                                    message.pushName ||
                                    message.notifyName ||
                                    message.author_name ||
                                    (!isOut && groupChat
                                        ? String(message.from || message.author || message.participant || "")
                                              .replace(/@.*$/, "")
                                        : "");
                                const isSearchTarget =
                                    searchFocus?.messageId != null &&
                                    String(message.id) === String(searchFocus.messageId);
                                const reactBtn = canReact ? (
                                    <button
                                        type="button"
                                        className="wa-react-hover-btn"
                                        title="React"
                                        aria-label="Add reaction"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openReactPicker(message, e);
                                        }}
                                    >
                                        <Icon icon="mdi:emoticon-outline" width={16} />
                                    </button>
                                ) : null;
                                return (
                                    <div
                                        key={message.temp_id || message.id || index}
                                        data-message-id={message.id || undefined}
                                        className={`wa-msg-row ${isOut ? "out" : "in"}${isSearchTarget ? " wa-msg-search-hit" : ""}`}
                                    >
                                        {!isOut && (
                                            <ChatAvatar
                                                user={
                                                    groupChat
                                                        ? {
                                                              name: senderName || "?",
                                                              type: "whatsapp",
                                                              profile_photo_url:
                                                                  message.sender_photo_url ||
                                                                  message.profile_photo_url,
                                                          }
                                                        : selectedUser
                                                }
                                                size={28}
                                                className="wa-msg-avatar-fallback"
                                            />
                                        )}
                                        {isOut ? reactBtn : null}
                                        <div
                                            className={`wa-bubble ${isOut ? "out" : "in"}${
                                                sendStatus === "pending" ? " pending" : ""
                                            }${sendStatus === "failed" ? " failed" : ""}`}
                                        >
                                            {!isOut && groupChat && senderName && (
                                                <div className="wa-bubble-sender">{senderName}</div>
                                            )}
                                            {renderMessageContent(message)}
                                            {Array.isArray(message.reactions) && message.reactions.length > 0 && (
                                                <div className="wa-msg-reactions" aria-label="Reactions">
                                                    {message.reactions.map((r, ri) => (
                                                        <span
                                                            key={`${r.emoji}-${r.fromMe ? "me" : r.sender_jid || ri}`}
                                                            className={`wa-msg-reaction ${r.fromMe ? "mine" : ""}`}
                                                            title={r.fromMe ? "You" : "Contact"}
                                                        >
                                                            {r.emoji}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="wa-bubble-meta">
                                                <span className="wa-bubble-time">
                                                    {formatTime(message.timestamp || message.created_at)}
                                                </span>
                                                {isOut && sendStatus === "pending" && (
                                                    <span className="wa-bubble-ticks pending" title="Sending…">
                                                        <Icon icon="mdi:clock-outline" width={14} />
                                                    </span>
                                                )}
                                                {isOut && sendStatus === "failed" && (
                                                    <>
                                                        <span className="wa-bubble-ticks failed" title={message.send_error || "Failed"}>
                                                            <Icon icon="mdi:alert-circle-outline" width={14} />
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="wa-msg-retry-btn"
                                                            onClick={() => retrySendMessage(message)}
                                                        >
                                                            <Icon icon="mdi:refresh" width={12} />
                                                            Retry
                                                        </button>
                                                    </>
                                                )}
                                                {isOut && sendStatus !== "pending" && sendStatus !== "failed" && (() => {
                                                    // Blue ticks only when WhatsApp reports recipient read
                                                    const delivery = String(message.delivery_status || "sent").toLowerCase();
                                                    const isRead = delivery === "read";
                                                    const isDelivered = delivery === "delivered" || isRead;
                                                    return (
                                                        <span
                                                            className={`wa-bubble-ticks ${isRead ? "" : "sent"}`}
                                                            title={
                                                                isRead
                                                                    ? "Seen"
                                                                    : isDelivered
                                                                      ? "Delivered"
                                                                      : "Sent"
                                                            }
                                                        >
                                                            {isDelivered ? "✓✓" : "✓"}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        {!isOut ? reactBtn : null}
                                        {isOut && (
                                            <ChatAvatar
                                                user={{
                                                    name: "You",
                                                    type: "whatsapp",
                                                    profile_photo_url: myProfilePhoto,
                                                    profile_photo: myProfilePhoto,
                                                }}
                                                size={28}
                                                className="wa-msg-avatar-fallback"
                                            />
                                        )}
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        <div className="wa-composer-wrap" ref={emojiPickerRef}>
                            {sendNotice ? (
                                <div className={`wa-send-notice wa-send-notice-${sendNotice.tone || "warning"}`}>
                                    <div className="wa-send-notice-icon">
                                        <Icon icon="mdi:clock-alert-outline" width={20} />
                                    </div>
                                    <div className="wa-send-notice-body">
                                        <strong>{sendNotice.title || "Notice"}</strong>
                                        <p>{sendNotice.message}</p>
                                    </div>
                                    <button
                                        type="button"
                                        className="wa-send-notice-close"
                                        aria-label="Dismiss"
                                        onClick={() => setSendNotice(null)}
                                    >
                                        <Icon icon="mdi:close" width={16} />
                                    </button>
                                </div>
                            ) : null}
                            {emojiOpen && !isRecording && (
                                <div className="wa-emoji-picker">
                                    <div className="wa-emoji-tabs">
                                        {EMOJI_CATEGORIES.map((cat) => (
                                            <button
                                                key={cat.id}
                                                type="button"
                                                className={`wa-emoji-tab ${emojiCategory === cat.id ? "active" : ""}`}
                                                title={cat.title}
                                                onClick={() => setEmojiCategory(cat.id)}
                                            >
                                                {cat.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="wa-emoji-grid">
                                        {(EMOJI_CATEGORIES.find((c) => c.id === emojiCategory)?.emojis || []).map(
                                            (emoji, index) => (
                                                <button
                                                    key={`${emoji}-${index}`}
                                                    type="button"
                                                    className="wa-emoji-item"
                                                    onClick={() => insertEmoji(emoji)}
                                                >
                                                    {emoji}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}

                            {!isRecording && quickReplies.length > 0 && (
                                <div className="wa-quick-replies" aria-label="Quick replies">
                                    {quickReplies.map((qr) => (
                                        <button
                                            key={qr.id}
                                            type="button"
                                            className="wa-quick-chip"
                                            title={qr.message}
                                            onClick={() => {
                                                setEmojiOpen(false);
                                                setNewMessage(qr.message || "");
                                                requestAnimationFrame(() => messageInputRef.current?.focus());
                                            }}
                                        >
                                            {qr.title}
                                        </button>
                                    ))}
                                </div>
                            )}

                        <footer className="wa-composer">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                                style={{ display: "none" }}
                                onChange={handleFileSelect}
                            />
                            {!isRecording ? (
                                <>
                                    <button
                                        type="button"
                                        className={`wa-icon-btn ${emojiOpen ? "active" : ""}`}
                                        aria-label="Emoji"
                                        aria-expanded={emojiOpen}
                                        onClick={() => {
                                            setAttachMenuOpen(false);
                                            setEmojiOpen((open) => !open);
                                        }}
                                    >
                                        <Icon icon={emojiOpen ? "mdi:keyboard-outline" : "mdi:emoticon-outline"} />
                                    </button>
                                    <div className="wa-composer-box">
                                        <input
                                            ref={messageInputRef}
                                            type="text"
                                            value={newMessage}
                                            onChange={(e) => {
                                                setNewMessage(e.target.value);
                                                notifyTypingFromInput(e.target.value);
                                            }}
                                            onFocus={() => {
                                                setEmojiOpen(false);
                                                setAttachMenuOpen(false);
                                                if (newMessage.trim()) notifyTypingFromInput(newMessage);
                                            }}
                                            onBlur={() => sendTypingState("paused")}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    if (!readUserPref("enter_to_send", true)) return;
                                                    e.preventDefault();
                                                    setEmojiOpen(false);
                                                    sendTypingState("paused");
                                                    handleSendMessage("text");
                                                }
                                            }}
                                            placeholder="Type a message"
                                        />
                                        <div className="wa-attach-wrap" ref={attachMenuRef}>
                                            <button
                                                type="button"
                                                className={`wa-icon-btn ${attachMenuOpen ? "active" : ""}`}
                                                aria-label="Attach"
                                                aria-expanded={attachMenuOpen}
                                                onClick={() => {
                                                    setEmojiOpen(false);
                                                    setAttachMenuOpen((open) => !open);
                                                }}
                                            >
                                                <Icon icon="mdi:paperclip" />
                                            </button>
                                            {attachMenuOpen ? (
                                                <div className="wa-attach-menu" role="menu">
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        className="wa-attach-menu-item"
                                                        onClick={openLibraryPicker}
                                                    >
                                                        <Icon icon="mdi:folder-image" width={20} />
                                                        <span>Media library</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        className="wa-attach-menu-item"
                                                        onClick={() => {
                                                            setAttachMenuOpen(false);
                                                            fileInputRef.current?.click();
                                                        }}
                                                    >
                                                        <Icon icon="mdi:laptop" width={20} />
                                                        <span>Local device</span>
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    {newMessage.trim() ? (
                                        <button
                                            type="button"
                                            className="wa-send-btn"
                                            onClick={() => {
                                                setEmojiOpen(false);
                                                handleSendMessage("text");
                                            }}
                                            aria-label="Send"
                                        >
                                            <Icon icon="mdi:send" />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="wa-icon-btn"
                                            onClick={() => {
                                                setEmojiOpen(false);
                                                handleStartRecording();
                                            }}
                                            aria-label="Record voice"
                                        >
                                            <Icon icon="mdi:microphone" />
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="wa-recording-bar">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="wa-recording-dot" />
                                        <span style={{ color: "#ea0038", fontSize: "0.875rem" }}>
                                            Recording {formatDuration(recordingDuration)}
                                        </span>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button type="button" className="wa-preview-btn delete" onClick={handleCancelRecording}>
                                            <Icon icon="mdi:close" />
                                        </button>
                                        <button type="button" className="wa-preview-btn send" onClick={handleStopRecording}>
                                            <Icon icon="mdi:check" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </footer>

                        {!isRecording && audioBlob && (
                            <div className="wa-composer" style={{ paddingTop: 0 }}>
                                <div className="wa-audio-preview w-100">
                                    <audio controls>
                                        <source src={URL.createObjectURL(audioBlob)} type="audio/wav" />
                                    </audio>
                                    <button type="button" className="wa-preview-btn delete" onClick={() => setAudioBlob(null)}>
                                        <Icon icon="mdi:delete-outline" />
                                    </button>
                                    <button type="button" className="wa-preview-btn send" onClick={handleSendRecording}>
                                        <Icon icon="mdi:send" />
                                    </button>
                                </div>
                            </div>
                        )}
                        </div>
                    </>
                )}
            </section>

            {libraryPickerOpen ? (
                <div
                    className="wa-library-overlay"
                    onClick={() => setLibraryPickerOpen(false)}
                    role="presentation"
                >
                    <div
                        className="wa-library-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Media library"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="wa-library-modal-header">
                            <h3>Media library</h3>
                            <button
                                type="button"
                                className="wa-icon-btn"
                                aria-label="Close"
                                onClick={() => setLibraryPickerOpen(false)}
                            >
                                <Icon icon="mdi:close" />
                            </button>
                        </div>
                        <div className="wa-library-kinds">
                            {LIBRARY_KINDS.map((k) => (
                                <button
                                    key={k.id}
                                    type="button"
                                    className={`wa-library-kind ${libraryKind === k.id ? "active" : ""}`}
                                    onClick={() => handleLibraryKindChange(k.id)}
                                >
                                    {k.label}
                                </button>
                            ))}
                        </div>
                        <div className="wa-library-grid">
                            {libraryLoading ? (
                                <p className="wa-library-empty">Loading…</p>
                            ) : libraryItems.length === 0 ? (
                                <p className="wa-library-empty">No media yet. Upload files in Media Library.</p>
                            ) : (
                                libraryItems.map((item) => {
                                    const kind = String(item.media_kind || "").toLowerCase();
                                    const src = getMediaUrl(item.url || item.relative_path);
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className="wa-library-item"
                                            onClick={() => sendFromLibrary(item)}
                                            title={item.title || item.filename}
                                        >
                                            {kind === "image" && src ? (
                                                <img src={src} alt={item.title || ""} />
                                            ) : (
                                                <div className="wa-library-item-icon">
                                                    <Icon
                                                        icon={
                                                            kind === "video"
                                                                ? "mdi:video-outline"
                                                                : kind === "audio"
                                                                  ? "mdi:music-note-outline"
                                                                  : "mdi:file-document-outline"
                                                        }
                                                        width={28}
                                                    />
                                                </div>
                                            )}
                                            <span className="wa-library-item-title">
                                                {item.title || item.filename || "File"}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            {incomingCall ? (
                <div className="wa-call-overlay" role="dialog" aria-modal="true" aria-label="Incoming call">
                    <div className="wa-call-card">
                        <div className={`wa-call-pulse ${incomingCall.is_video ? "video" : ""}`}>
                            <Icon
                                icon={incomingCall.is_video ? "mdi:video" : "mdi:phone-incoming"}
                                width={36}
                            />
                        </div>
                        <h3>{incomingCall.name || "Incoming call"}</h3>
                        <p>
                            {incomingCall.is_video ? "Incoming video call" : "Incoming voice call"}
                            {incomingCall.is_group ? " · Group" : ""}
                        </p>
                        <div className="wa-call-actions">
                            <button
                                type="button"
                                className="wa-call-reject"
                                disabled={callActionLoading}
                                onClick={rejectIncomingCall}
                            >
                                <Icon icon="mdi:phone-hangup" width={22} />
                                Reject
                            </button>
                            <button
                                type="button"
                                className="wa-call-open"
                                onClick={() => {
                                    const phone = String(incomingCall.caller_pn || incomingCall.sid || "")
                                        .replace(/\D/g, "");
                                    if (phone) {
                                        window.open(`https://wa.me/${phone}`, "_blank", "noopener,noreferrer");
                                    }
                                    showToast("Answer the call in the WhatsApp app on your phone", "success");
                                }}
                            >
                                <Icon icon="mdi:whatsapp" width={22} />
                                Open WhatsApp
                            </button>
                        </div>
                        <button
                            type="button"
                            className="wa-call-dismiss"
                            onClick={() => setIncomingCall(null)}
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export default ChatSystem

